import { useState } from "react";
import { Navigation } from "./Index/components/Navigation";
import { Hero } from "./Index/components/Hero";
import { TrustBadges } from "./Index/components/TrustBadges";
import { Nursery13Banner } from "./Index/components/Nursery13Banner";
import { ValueProposition } from "./Index/components/ValueProposition";
import { HowWeTeach } from "./Index/components/HowWeTeach";
import { TrilingualModel } from "./Index/components/TrilingualModel";
import { NEASCSection } from "./Index/components/NEASCSection";
import { Gallery } from "./Index/components/Gallery";
import { Principles } from "./Index/components/Principles";
import { Testimonials } from "./Index/components/Testimonials";
import { CTASection } from "./Index/components/CTASection";
import { Footer } from "./Index/components/Footer";
import { OpenHouseBanner } from "@/components/OpenHouseBanner";
import { useLanguage } from "@/contexts/LanguageContext";
import { usePageMeta } from "@/hooks/usePageMeta";

const META: Record<string, { title: string; description: string }> = {
  es: {
    title: "Gándara International School | Colegio Internacional en Vigo",
    description: "Escuela internacional Montessori de infantil y primaria en Gondomar (Pontevedra). Acreditada por NEASC. Educación trilingüe, pedagogía activa y 5.000m² de naturaleza.",
  },
  en: {
    title: "Gándara International School | International School near Vigo",
    description: "Montessori-inspired international school in Gondomar (Pontevedra). NEASC accredited. Trilingual education, active pedagogy, 5,000m² natural campus.",
  },
  pt: {
    title: "Gándara International School | Escola Internacional em Vigo",
    description: "Escola internacional Montessori de educação infantil e primária em Gondomar (Pontevedra). Acreditada pela NEASC. Educação trilingue e pedagogia ativa.",
  },
  fr: {
    title: "Gándara International School | École Internationale à Vigo",
    description: "École internationale Montessori maternelle et primaire à Gondomar (Pontevedra). Accréditée NEASC. Éducation trilingue et pédagogie active.",
  },
};

const Index = () => {
  const [isBannerVisible, setIsBannerVisible] = useState(true);
  const { language } = useLanguage();
  const meta = META[language] || META.es;
  usePageMeta({ title: meta.title, description: meta.description });

  const schoolJsonLd = {
    "@context": "https://schema.org",
    "@type": ["EducationalOrganization", "School"],
    "name": "Gándara International School",
    "alternateName": "Gándara Kids Journey",
    "url": "https://gandaraschool.com",
    "logo": "https://gandaraschool.com/logo.png",
    "image": "https://gandaraschool.com/og-gandara.png",
    "description": meta.description,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Lugar de Moureira, 1, Mañufe",
      "addressLocality": "Gondomar",
      "addressRegion": "Pontevedra",
      "postalCode": "36388",
      "addressCountry": "ES"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 42.1167,
      "longitude": -8.7500
    },
    "telephone": ["+34 986 369 778", "+34 676 367 580"],
    "email": "info@gandaraschool.com",
    "foundingDate": "2017",
    "areaServed": ["Gondomar", "Vigo", "Val Miñor", "Pontevedra"],
    "hasCredential": {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "Accreditation",
      "recognizedBy": {
        "@type": "Organization",
        "name": "New England Association of Schools and Colleges (NEASC)",
        "url": "https://www.neasc.org"
      }
    },
    "teaches": ["Montessori Education", "Trilingual Education", "Primary Education", "Early Childhood Education"],
    "availableLanguage": ["Spanish", "English", "Galician"],
    "priceRange": "€€",
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "16:30"
    },
    "sameAs": [
      "https://www.instagram.com/gandaraischool/",
      "https://www.facebook.com/espaciogandara"
    ]
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schoolJsonLd) }}
      />
      <Navigation />
      <OpenHouseBanner onVisibilityChange={setIsBannerVisible} />
      <div className={`min-h-screen ${isBannerVisible ? 'pt-[120px]' : 'pt-20'}`}>
        <Hero />
        <TrustBadges />
        <Nursery13Banner />
        {/* Social proof first - credibility */}
        <Testimonials />
        {/* Why choose us */}
        <ValueProposition />
        {/* How we do it */}
        <HowWeTeach />
        <TrilingualModel />
        {/* Visual proof */}
        <Gallery />
        {/* Values */}
        <Principles />
        {/* International credibility */}
        <NEASCSection />
        {/* Final CTA */}
        <CTASection />
        <Footer />
      </div>
    </>
  );
};

export default Index;
